import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { FirebaseError } from 'firebase/app';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import Routes, { DELIVERY_ID_PARAM, SET_ID_PARAM } from 'routes';
import API from 'api/API';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import useFetchDelivery from 'api/hooks/useFetchDelivery';
import { useUpdateTrayInspection } from 'api/hooks/useUpdateTrayInspection';
import { TrayInspectionStage } from 'api/models/Deliveries';
import { TrayImagePathParams, TrayInspectionInfo } from 'api/models/Trays';
import { useLanguage } from 'context/AppContext';
import { CheckOutTray, CheckOutTraysBySet, useCheckOutTraysBySet } from 'hooks/useCheckOutTraysBySet';
import { useRefreshFirebaseToken } from 'hooks/useRefreshFirebaseToken';
import { useVisibleCheckOutDialog } from 'hooks/useVisibleCheckOutDialog';
import { useCheckOutFlowParams } from 'hooks/useCheckOutFlowParams';
import useCapturedTrayImage from 'hooks/useCapturedTrayImage';
import { useCheckOut } from './useCheckOut';
import { useHistoryStack } from 'hooks/useHistoryStack';

const useTraySubmitPage = (inOrOut: 'in' | 'out') => {
  const isCheckIn = inOrOut == 'in';
  const stage: TrayInspectionStage = isCheckIn ? 'CHECK_IN' : 'CHECK_OUT';

  const [language] = useLanguage();
  const lang = language.CheckOutTraySubmitPage;

  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState<string>('');

  const { deliveryId = '', setId = '', trayId = '' } = useParams();

  const { capturedImage, setCapturedImage, uploadCapturedTrayImage } = useCapturedTrayImage();

  const { data: user } = useCurrentUser();
  const userId = user?.userId || '';
  const tenantName = user?.tenantName || '';

  const { mutateAsync: updateTrayInspection } = useUpdateTrayInspection();

  const { refreshFirebaseToken } = useRefreshFirebaseToken();
  const navigate = useNavigate();

  const { delivery, set, tray, inspection } = useFetchDelivery(deliveryId, setId, trayId, stage);

  const [checkOutTraysBySet, setCheckOutTraysBySet] = useCheckOutTraysBySet();
  const checkOutSet = (setId && checkOutTraysBySet[setId]) || undefined;
  const checkOutTray = (checkOutSet && trayId && checkOutSet.trays[trayId]) || undefined;
  const { checkOutFlowParams } = useCheckOutFlowParams();

  const { completeCheckOutForSet } = useCheckOut({ deliveryId });

  // in the case where we are captureing data into a new checkOutTraysBySet
  //  state, populate the checkOutTray inspection values from the UI
  useEffect(() => {
    if (!checkOutTray) return;

    const _checkOutTray = { ...checkOutTray };
    let changed = false;

    if (capturedImage?.dataUri && !checkOutTray?.imageDataUri) {
      const _checkOutTraysBySet = { ...checkOutTraysBySet };
      const _checkOutTray = { ...checkOutTray, imageDataUri: capturedImage.dataUri };

      _checkOutTraysBySet[setId].trays[trayId] = _checkOutTray;
      setCheckOutTraysBySet(_checkOutTraysBySet);
    }

    // the default state values
    if (_checkOutTray.allDevicesCleanOfDebris == null) {
      _checkOutTray.allDevicesCleanOfDebris = true;
      _checkOutTray.allDevicesFunctional = true;
      _checkOutTray.allDevicesInTray = true;
      changed = true;
    }

    // the comments
    if (comments != _checkOutTray.comments) {
      //_checkOutTray.comments = comments;
      //changed = true;
    }

    if (changed) {
      const _checkOutTraysBySet = { ...checkOutTraysBySet };
      _checkOutTraysBySet[setId].trays[trayId] = _checkOutTray;
      setCheckOutTraysBySet(_checkOutTraysBySet);
    }
  }, [capturedImage, checkOutTray, checkOutTraysBySet, comments, setCheckOutTraysBySet, setId, trayId]);

  // in the case where the view data is coming from the checkOutTraysBySet
  //  state, load the image data into the capturedImage state
  useEffect(() => {
    // Synchronize state with the captured image URL if not already set
    if (capturedImage || !checkOutTray?.imageDataUri) return;

    setCapturedImage({
      dataUri: checkOutTray.imageDataUri,
      aspectRatio: 1,
      isLandscape: true,
    });
  }, [capturedImage, checkOutTray, setCapturedImage]);

  const inspectionValues = useMemo(() => {
    return checkOutTray
      ? ({
          allClean: checkOutTray.allDevicesCleanOfDebris ?? true,
          allFunctional: checkOutTray.allDevicesFunctional ?? true,
          allPartsPresent: checkOutTray.allDevicesInTray ?? true,
          trayUsed: checkOutTray.trayUsed ?? true,
          comments: checkOutTray.comments || '',
          gsImagePath: checkOutTray?.gsImagePath || '',
        } as TrayInspectionInfo)
      : inspection;
  }, [checkOutTray, inspection]);

  const onInspectionValueChange = useCallback(
    (key: string, value: boolean | string | string[]) => {
      if (!checkOutTray) return;

      if (key == 'allClean') key = 'allDevicesCleanOfDebris';
      else if (key == 'allFunctional') key = 'allDevicesFunctional';
      else if (key == 'allPartsPresent') key = 'allDevicesInTray';
      else if (key != 'missingParts' && key != 'missingPartsComment' && key != 'comments' && key != 'completed') return;

      const checkOutTraysBySetUpdate = { ...checkOutTraysBySet };
      const checkOutTrayUpdate = { ...checkOutTray, [key]: value };
      checkOutTraysBySetUpdate[setId].trays[trayId] = checkOutTrayUpdate;
      setCheckOutTraysBySet(checkOutTraysBySetUpdate);
    },
    [checkOutTray, checkOutTraysBySet, setCheckOutTraysBySet, setId, trayId],
  );

  const historyStack = useHistoryStack()[0];
  const { haveCheckOutDialogHistory, prevVisibleCheckOutDialog, setVisibleCheckOutDialog } = useVisibleCheckOutDialog();

  const navigateBack = useCallback(
    (go = 1) => {
      if (haveCheckOutDialogHistory) {
        const referrer = historyStack.reverse().find(h => !h.pathname.includes('/check-out-tray-submit/'));
        prevVisibleCheckOutDialog();
        if (referrer) {
          navigate(referrer.pathname);
          return;
        }
      }

      const route = go == 2 ? `check-${inOrOut}-tray` : `capture-tray-image/check-${inOrOut}`;
      navigate(`/${route}/${deliveryId}/${setId}/${trayId}`);
    },
    [historyStack, haveCheckOutDialogHistory, prevVisibleCheckOutDialog, inOrOut, navigate, deliveryId, setId, trayId],
  );

  // v1 save-as-you-go handler
  const handleFormSubmit = useCallback(async () => {
    if (!deliveryId || !setId || !trayId || !inspection || !capturedImage?.dataUri || !delivery) return;

    setSaving(true);

    // upload image to blob storage
    const pathParams: TrayImagePathParams = {
      stage,
      deliveryId,
      setId,
      trayId,
    };

    let gsImagePath;

    try {
      gsImagePath = await uploadCapturedTrayImage(pathParams);
    } catch (error) {
      // if this for some reason fails because of a storage/unauthorized error, try to sign in again
      if ((error as FirebaseError).code === 'storage/unauthorized') {
        console.error('Storage unauthorized, trying to sign in again...');

        await refreshFirebaseToken();

        gsImagePath = await uploadCapturedTrayImage(pathParams);
      } else {
        API.errorHandler(error);
      }
    }

    if (!gsImagePath) {
      setSaving(false);

      return;
    }

    // save the image url in tray inspection info
    const ins: TrayInspectionInfo = {
      ...inspection,
      gsImagePath,
      status: 'CONFIRMED',
      comments,
      missingParts: inspection.missingParts || [],
    };
    await updateTrayInspection({
      trayId,
      inspectionInfo: ins,
    });

    const checkInFlow = inOrOut == 'in';
    const eventType = checkInFlow ? EventType.CompleteTrayCheckIn : EventType.CompleteTrayCheckOut;

    await trackEvent(eventType, {
      status: delivery.status,
      order_id: delivery.orderNumber,
      received_by: delivery?.receivedDetails?.receivedBy || '',
      set_id: set?.kitInstanceId || '',
      tray_instance_id: trayId,
      all_clean: inspection?.allClean,
      all_functional: inspection.allFunctional,
      all_parts_present: inspection.allPartsPresent,
      tray_used: inspection.trayUsed,
      current_user: userId,
      tenant_name: tenantName,
    });

    toast.success(lang.savedTrayInspection);
    navigate(Routes.SetDetails.path.replace(DELIVERY_ID_PARAM, deliveryId).replace(SET_ID_PARAM, setId));

    setSaving(false);
  }, [
    capturedImage?.dataUri,
    comments,
    delivery,
    deliveryId,
    inOrOut,
    inspection,
    lang.savedTrayInspection,
    navigate,
    refreshFirebaseToken,
    set?.kitInstanceId,
    setId,
    stage,
    tenantName,
    trayId,
    updateTrayInspection,
    uploadCapturedTrayImage,
    userId,
  ]);

  // v2 update checkOutTraysBySet state and continue on checkout flow
  const continueCheckOutFlow = useCallback(async () => {
    if (!checkOutTray) {
      throw new Error('No check out tray found');
    }

    // add the comments
    const checkOutTraysBySetUpdate: CheckOutTraysBySet = { ...checkOutTraysBySet };
    const checkOutTrayUpdate: CheckOutTray = { ...checkOutTray, comments, completed: true };

    checkOutTraysBySetUpdate[setId].trays[trayId] = checkOutTrayUpdate;

    setCheckOutTraysBySet(checkOutTraysBySetUpdate);

    const route =
      checkOutFlowParams.flowType == 'SURGERY'
        ? Routes.Surgery.makePath(delivery?.orderRecordId || '')
        : Routes.CheckOut.makePath(deliveryId);

    const haveRemainingTrays = Object.values(checkOutTraysBySet[setId].trays).some(
      tray => tray.trayInstanceId != trayId && !tray.completed,
    );

    if (haveRemainingTrays) {
      setVisibleCheckOutDialog({
        type: 'SELECT_TRAY',
        setKitInstanceId: setId,
      });
    } else {
      const isSetPendingTurnaround = set?.turnaroundOrigin && set?.turnaroundStage === 'PENDING';

      if (isSetPendingTurnaround) {
        setVisibleCheckOutDialog({
          type: 'SET_ALREADY_TURNED_AROUND_DIALOG',
          setKitInstanceId: setId,
        });
      } else {
        // We have to navigate before we call completeCheckOutForSet so that our save dialog shows
        navigate(route);

        await completeCheckOutForSet(setId);

        return;
      }
    }

    navigate(route);
  }, [
    checkOutTray,
    checkOutTraysBySet,
    comments,
    setId,
    trayId,
    setCheckOutTraysBySet,
    checkOutFlowParams.flowType,
    delivery?.orderRecordId,
    deliveryId,
    navigate,
    setVisibleCheckOutDialog,
    set?.turnaroundOrigin,
    set?.turnaroundStage,
    completeCheckOutForSet,
  ]);

  return {
    lang,
    delivery,
    set,
    tray,
    inspection,
    inspectionValues,
    onInspectionValueChange: checkOutTray && onInspectionValueChange,
    comments,
    setComments,
    saving,
    handleFormSubmit: checkOutTray ? continueCheckOutFlow : handleFormSubmit,
    navigateBack,
    capturedImage,
    setCapturedImage,
  };
};

export default useTraySubmitPage;
